import { communicationChartAudienceKey } from '@web/components/CommunicationNetworkGraph/components/communicationChartI18n';
import { COMMUNICATION_CHART_EXTERNAL_NODE_ID, COMMUNICATION_CHART_LABEL_MAX_LENGTH } from '@web/components/CommunicationNetworkGraph/core/communicationChart/constants';
import { getCommunicationChartLinkEdgeId } from '@web/components/CommunicationNetworkGraph/core/communicationChart/linkEdgeId';
import { findCommunicationChartLinkBetween } from '@web/components/CommunicationNetworkGraph/core/communicationChart/linkLookup';
import { CommunicationChartNodeKindEnum } from '@web/components/CommunicationNetworkGraph/core/communicationChart/nodeKind';
import { isCommunicationChartEmployeeScope, isCommunicationChartOrgScope } from '@web/components/CommunicationNetworkGraph/core/communicationChart/types';
import { applyCommunicationChartRingPositions, buildCommunicationChartElements } from '@web/components/CommunicationNetworkGraph/graph/buildCommunicationChartElements';
import { preloadCommunicationChartAvatarImages } from '@web/components/CommunicationNetworkGraph/graph/communicationChartAvatarImageCache';
import { COMMUNICATION_CHART_EXTERNAL_NODE_RADIUS, COMMUNICATION_CHART_HEIGHT_PX, COMMUNICATION_CHART_INTERNAL_MEET_NODE_RADIUS, COMMUNICATION_CHART_NODE_SIZE_MAX, COMMUNICATION_CHART_NODE_SIZE_MIN, COMMUNICATION_CHART_OTHER_NODE_SIZE } from '@web/components/CommunicationNetworkGraph/graph/communicationChartConstants';
import { COMMUNICATION_CHART_CYTOSCAPE_STYLES } from '@web/components/CommunicationNetworkGraph/graph/communicationChartCytoscapeStyles';
import { getCommunicationChartLinkFromEdge } from '@web/components/CommunicationNetworkGraph/graph/communicationChartLinkEdgeData';
import { animateCommunicationChartNodeHoverIn, animateCommunicationChartNodeHoverOut, playCommunicationChartEntranceAnimation } from '@web/components/CommunicationNetworkGraph/graph/communicationChartMotion';
import { applyCommunicationChartOrgTeamHubVisibility, resolveCommunicationChartOrgTeamHubEdgeTeamId, resolveCommunicationChartOrgTeamHubRevealTeamIds, resolveCommunicationChartOrgTeamHubSatelliteNodeId } from '@web/components/CommunicationNetworkGraph/graph/communicationChartOrgTeamHubVisibility';
import { drawCommunicationChartCanvasOverlay } from '@web/components/CommunicationNetworkGraph/graph/drawCommunicationChartArrowOverlay';
import { fitCommunicationChartViewport } from '@web/components/CommunicationNetworkGraph/graph/fitCommunicationChartViewport';
import cytoscape from 'cytoscape';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
export var useCommunicationChartCy = function (_a) {
    var data = _a.data, selectedNodeId = _a.selectedNodeId, onSelectedNodeIdChange = _a.onSelectedNodeIdChange, pinnedHubTeamId = _a.pinnedHubTeamId, onPinnedHubTeamIdChange = _a.onPinnedHubTeamIdChange, pinnedLink = _a.pinnedLink, onPinnedLinkChange = _a.onPinnedLinkChange;
    var t = useTranslation().t;
    var containerRef = useRef(null);
    var arrowOverlayRef = useRef(null);
    var cyRef = useRef(null);
    var entranceAnimationRef = useRef(null);
    var containerSizeRef = useRef({
        width: 960,
        height: COMMUNICATION_CHART_HEIGHT_PX
    });
    var displayDataRef = useRef(data);
    var selectedNodeIdRef = useRef(selectedNodeId);
    selectedNodeIdRef.current = selectedNodeId;
    var pinnedHubTeamIdRef = useRef(pinnedHubTeamId);
    pinnedHubTeamIdRef.current = pinnedHubTeamId;
    var pinnedLinkRef = useRef(pinnedLink);
    pinnedLinkRef.current = pinnedLink;
    var hoveredOrgTeamIdRef = useRef(null);
    var hubRevealClearTimeoutRef = useRef(null);
    var applyOrgTeamHubRevealRef = useRef(function () { });
    var _b = useState(null), hoverTooltip = _b[0], setHoverTooltip = _b[1];
    var _c = useState(false), chartInitError = _c[0], setChartInitError = _c[1];
    var _d = useState(containerSizeRef.current), containerSize = _d[0], setContainerSize = _d[1];
    var handlersRef = useRef({
        selectedNodeId: selectedNodeId,
        pinnedHubTeamId: pinnedHubTeamId,
        onSelectedNodeIdChange: onSelectedNodeIdChange,
        onPinnedHubTeamIdChange: onPinnedHubTeamIdChange,
        onPinnedLinkChange: onPinnedLinkChange,
        setHoverTooltip: setHoverTooltip,
        t: t
    });
    displayDataRef.current = data;
    containerSizeRef.current = containerSize;
    handlersRef.current = {
        selectedNodeId: selectedNodeId,
        pinnedHubTeamId: pinnedHubTeamId,
        onSelectedNodeIdChange: onSelectedNodeIdChange,
        onPinnedHubTeamIdChange: onPinnedHubTeamIdChange,
        onPinnedLinkChange: onPinnedLinkChange,
        setHoverTooltip: setHoverTooltip,
        t: t
    };
    useEffect(function () {
        var element = containerRef.current;
        if (!element) {
            return;
        }
        var observer = new ResizeObserver(function (entries) {
            var entry = entries[0];
            if (!entry) {
                return;
            }
            setContainerSize({
                width: Math.max(entry.contentRect.width, 320),
                height: Math.max(entry.contentRect.height, COMMUNICATION_CHART_HEIGHT_PX)
            });
        });
        observer.observe(element);
        return function () { return observer.disconnect(); };
    }, []);
    var redrawCanvasOverlay = useCallback(function () {
        var cy = cyRef.current;
        var canvas = arrowOverlayRef.current;
        if (!cy || !canvas) {
            return;
        }
        drawCommunicationChartCanvasOverlay(cy, canvas);
    }, []);
    var applyOrgTeamHubReveal = useCallback(function () {
        var _a;
        var cy = cyRef.current;
        if (!cy || !isCommunicationChartOrgScope(displayDataRef.current.scope)) {
            return;
        }
        var pinnedLinkHubTeamId = pinnedLinkRef.current
            ? resolveCommunicationChartOrgTeamHubEdgeTeamId(pinnedLinkRef.current)
            : null;
        var revealedTeamIds = resolveCommunicationChartOrgTeamHubRevealTeamIds(cy, selectedNodeIdRef.current, hoveredOrgTeamIdRef.current, (_a = pinnedHubTeamIdRef.current) !== null && _a !== void 0 ? _a : pinnedLinkHubTeamId);
        applyCommunicationChartOrgTeamHubVisibility(cy, revealedTeamIds);
        redrawCanvasOverlay();
    }, [redrawCanvasOverlay]);
    applyOrgTeamHubRevealRef.current = applyOrgTeamHubReveal;
    var applySelectionStyles = useCallback(function (nodeId, activeLink) {
        var cy = cyRef.current;
        if (!cy) {
            return;
        }
        cy.elements().removeClass('highlighted dimmed selectedNode');
        if (activeLink) {
            var selectedEdge = cy.getElementById(getCommunicationChartLinkEdgeId(activeLink, data.channel));
            if (!selectedEdge.empty()) {
                selectedEdge.addClass('highlighted');
                var endpointNodes = selectedEdge.connectedNodes();
                endpointNodes.addClass('highlighted selectedNode');
            }
        }
        else if (nodeId !== null) {
            var selectedNode = cy.getElementById(String(nodeId));
            if (!selectedNode.empty()) {
                selectedNode.addClass('selectedNode highlighted');
                var incidentEdges = selectedNode.connectedEdges();
                incidentEdges.addClass('highlighted');
                incidentEdges.connectedNodes().addClass('highlighted');
            }
        }
        if (activeLink !== null || nodeId !== null) {
            cy.elements().forEach(function (element) {
                if (element.hasClass('highlighted')) {
                    return;
                }
                element.addClass('dimmed');
            });
        }
        applyOrgTeamHubRevealRef.current();
    }, [data.channel]);
    useEffect(function () {
        if (!containerRef.current) {
            return;
        }
        try {
            var cy_1 = cytoscape({
                container: containerRef.current,
                elements: [],
                minZoom: 0.4,
                maxZoom: 2.5,
                wheelSensitivity: 0.25,
                style: COMMUNICATION_CHART_CYTOSCAPE_STYLES
            });
            cy_1.on('tap', 'node', function (event) {
                event.stopPropagation();
                var node = event.target;
                var nodeId = Number(node.data('nodeId'));
                var handlers = handlersRef.current;
                var chartData = displayDataRef.current;
                handlers.setHoverTooltip(null);
                if (node.hasClass('teamHubSatellite')) {
                    handlers.onPinnedLinkChange(null);
                    var hubTeamId = node.data('hubTeamId');
                    var resolvedHubTeamId = typeof hubTeamId === 'number' ? hubTeamId : null;
                    var isSelected = handlers.selectedNodeId === nodeId;
                    if (isSelected) {
                        handlers.onSelectedNodeIdChange(null);
                        handlers.onPinnedHubTeamIdChange(null);
                    }
                    else {
                        handlers.onSelectedNodeIdChange(nodeId);
                        handlers.onPinnedHubTeamIdChange(resolvedHubTeamId);
                    }
                    return;
                }
                if (node.hasClass('orgTeam')) {
                    handlers.onPinnedLinkChange(null);
                    var nextSelectedNodeId = handlers.selectedNodeId === nodeId ? null : nodeId;
                    handlers.onSelectedNodeIdChange(nextSelectedNodeId);
                    handlers.onPinnedHubTeamIdChange(nextSelectedNodeId === null ? null : nodeId);
                    return;
                }
                if (isCommunicationChartEmployeeScope(chartData.scope) &&
                    chartData.focusUserId != null &&
                    nodeId !== chartData.focusUserId) {
                    var focusLink = findCommunicationChartLinkBetween(chartData.links, chartData.focusUserId, nodeId);
                    if (!focusLink &&
                        nodeId === COMMUNICATION_CHART_EXTERNAL_NODE_ID &&
                        chartData.nodes.some(function (node) { return node.nodeId === COMMUNICATION_CHART_EXTERNAL_NODE_ID; })) {
                        focusLink = {
                            sourceNodeId: chartData.focusUserId,
                            targetNodeId: COMMUNICATION_CHART_EXTERNAL_NODE_ID,
                            stats: { weight: 0, previousWeight: 0 }
                        };
                    }
                    if (focusLink) {
                        handlers.onPinnedHubTeamIdChange(null);
                        handlers.onSelectedNodeIdChange(chartData.focusUserId);
                        handlers.onPinnedLinkChange(focusLink);
                        return;
                    }
                }
                handlers.onPinnedLinkChange(null);
                handlers.onPinnedHubTeamIdChange(null);
                handlers.onSelectedNodeIdChange(handlers.selectedNodeId === nodeId ? null : nodeId);
            });
            cy_1.on('tap', 'edge', function (event) {
                event.stopPropagation();
                var edge = event.target;
                var handlers = handlersRef.current;
                if (edge.hasClass('teamHubEdge')) {
                    var link = getCommunicationChartLinkFromEdge(edge);
                    var satelliteNodeId = resolveCommunicationChartOrgTeamHubSatelliteNodeId(link);
                    var hubTeamId = resolveCommunicationChartOrgTeamHubEdgeTeamId(link);
                    if (satelliteNodeId !== null) {
                        handlers.onPinnedLinkChange(null);
                        handlers.onSelectedNodeIdChange(satelliteNodeId);
                        handlers.onPinnedHubTeamIdChange(hubTeamId);
                        handlers.setHoverTooltip(null);
                        return;
                    }
                }
                handlers.onSelectedNodeIdChange(null);
                handlers.onPinnedHubTeamIdChange(null);
                handlers.onPinnedLinkChange(getCommunicationChartLinkFromEdge(edge));
                handlers.setHoverTooltip(null);
            });
            cy_1.on('tap', function (event) {
                if (event.target === cy_1) {
                    if (hubRevealClearTimeoutRef.current !== null) {
                        window.clearTimeout(hubRevealClearTimeoutRef.current);
                        hubRevealClearTimeoutRef.current = null;
                    }
                    hoveredOrgTeamIdRef.current = null;
                    handlersRef.current.onSelectedNodeIdChange(null);
                    handlersRef.current.onPinnedHubTeamIdChange(null);
                    handlersRef.current.onPinnedLinkChange(null);
                    handlersRef.current.setHoverTooltip(null);
                    applyOrgTeamHubRevealRef.current();
                }
            });
            var setHoveredOrgTeamId_1 = function (teamId) {
                if (hubRevealClearTimeoutRef.current !== null) {
                    window.clearTimeout(hubRevealClearTimeoutRef.current);
                    hubRevealClearTimeoutRef.current = null;
                }
                hoveredOrgTeamIdRef.current = teamId;
                applyOrgTeamHubRevealRef.current();
            };
            var scheduleClearHoveredOrgTeamId_1 = function (teamId) {
                if (hubRevealClearTimeoutRef.current !== null) {
                    window.clearTimeout(hubRevealClearTimeoutRef.current);
                }
                hubRevealClearTimeoutRef.current = window.setTimeout(function () {
                    if (hoveredOrgTeamIdRef.current === teamId) {
                        hoveredOrgTeamIdRef.current = null;
                        applyOrgTeamHubRevealRef.current();
                    }
                }, 120);
            };
            cy_1.on('mouseover', 'node', function (event) {
                var _a, _b, _c;
                var node = event.target;
                node.addClass('hovered');
                animateCommunicationChartNodeHoverIn(node);
                redrawCanvasOverlay();
                if (node.hasClass('orgTeam')) {
                    setHoveredOrgTeamId_1(Number(node.data('nodeId')));
                }
                else if (node.hasClass('teamHubSatellite')) {
                    var hubTeamId = node.data('hubTeamId');
                    if (typeof hubTeamId === 'number') {
                        setHoveredOrgTeamId_1(hubTeamId);
                    }
                }
                var position = (_a = event.renderedPosition) !== null && _a !== void 0 ? _a : event.position;
                if (node.hasClass(CommunicationChartNodeKindEnum.External)) {
                    var fullLabel_1 = String((_b = node.data('fullLabel')) !== null && _b !== void 0 ? _b : handlersRef.current.t(communicationChartAudienceKey('external')));
                    handlersRef.current.setHoverTooltip({
                        x: position.x,
                        y: position.y,
                        label: fullLabel_1,
                        externalNode: {
                            name: fullLabel_1,
                            description: handlersRef.current.t('app.dashboard.communicationChart.tooltip.externalContactsDescription')
                        }
                    });
                    return;
                }
                if (node.hasClass(CommunicationChartNodeKindEnum.CrossTeamAggregate)) {
                    var fullLabel_2 = String((_c = node.data('fullLabel')) !== null && _c !== void 0 ? _c : handlersRef.current.t(communicationChartAudienceKey('crossTeam')));
                    handlersRef.current.setHoverTooltip({
                        x: position.x,
                        y: position.y,
                        label: fullLabel_2,
                        crossTeamAggregateNode: {
                            name: fullLabel_2,
                            description: handlersRef.current.t('app.dashboard.communicationChart.tooltip.crossTeamContactsDescription')
                        }
                    });
                    return;
                }
                var fullLabel = node.data('fullLabel');
                if (!fullLabel || !node.hasClass('personNode')) {
                    return;
                }
                if (node.hasClass('orgTeam')) {
                    var memberCount = node.data('memberCount');
                    if (typeof memberCount !== 'number') {
                        return;
                    }
                    handlersRef.current.setHoverTooltip({
                        x: position.x,
                        y: position.y,
                        label: fullLabel,
                        orgTeam: { name: fullLabel, count: memberCount }
                    });
                    return;
                }
                if (node.hasClass('highlighted')) {
                    return;
                }
                handlersRef.current.setHoverTooltip({
                    x: position.x,
                    y: position.y,
                    label: fullLabel
                });
            });
            cy_1.on('mouseout', 'node', function (event) {
                var node = event.target;
                node.removeClass('hovered');
                animateCommunicationChartNodeHoverOut(node);
                redrawCanvasOverlay();
                handlersRef.current.setHoverTooltip(null);
                if (node.hasClass('orgTeam')) {
                    scheduleClearHoveredOrgTeamId_1(Number(node.data('nodeId')));
                }
                else if (node.hasClass('teamHubSatellite')) {
                    var hubTeamId = node.data('hubTeamId');
                    if (typeof hubTeamId === 'number') {
                        scheduleClearHoveredOrgTeamId_1(hubTeamId);
                    }
                }
            });
            cy_1.on('render pan zoom resize', function () {
                var canvas = arrowOverlayRef.current;
                if (canvas) {
                    drawCommunicationChartCanvasOverlay(cy_1, canvas);
                }
            });
            cyRef.current = cy_1;
            return function () {
                var _a;
                if (hubRevealClearTimeoutRef.current !== null) {
                    window.clearTimeout(hubRevealClearTimeoutRef.current);
                    hubRevealClearTimeoutRef.current = null;
                }
                (_a = entranceAnimationRef.current) === null || _a === void 0 ? void 0 : _a.cancel();
                entranceAnimationRef.current = null;
                cy_1.destroy();
                cyRef.current = null;
            };
        }
        catch (error) {
            if (process.env.NODE_ENV === 'development') {
                // eslint-disable-next-line no-console -- surfaced when debugging chart init failures
                console.error('Communication chart failed to initialize cytoscape', error);
            }
            setChartInitError(true);
            return undefined;
        }
    }, [redrawCanvasOverlay]);
    useEffect(function () {
        var _a;
        var cy = cyRef.current;
        if (!cy) {
            return;
        }
        setChartInitError(false);
        try {
            setHoverTooltip(null);
            var _b = containerSizeRef.current, width = _b.width, height = _b.height;
            var elements_1 = buildCommunicationChartElements({
                data: data,
                width: width,
                height: height,
                labelMaxLength: COMMUNICATION_CHART_LABEL_MAX_LENGTH,
                nodeSizeMin: COMMUNICATION_CHART_NODE_SIZE_MIN,
                nodeSizeMax: COMMUNICATION_CHART_NODE_SIZE_MAX,
                externalNodeSize: COMMUNICATION_CHART_EXTERNAL_NODE_RADIUS * 2,
                internalMeetNodeSize: COMMUNICATION_CHART_INTERNAL_MEET_NODE_RADIUS * 2,
                otherNodeSize: COMMUNICATION_CHART_OTHER_NODE_SIZE
            }).elements;
            cy.batch(function () {
                cy.elements().remove();
                cy.add(elements_1);
            });
            if (isCommunicationChartEmployeeScope(data.scope)) {
                cy.scratch('employeeSectionLabels', {
                    teammates: t('app.dashboard.communicationChart.employeeSections.teammates'),
                    external: t('app.dashboard.communicationChart.employeeSections.external'),
                    crossTeamColleagues: t('app.dashboard.communicationChart.employeeSections.crossTeamColleagues')
                });
            }
            else {
                cy.scratch('employeeSectionLabels', null);
            }
            applyCommunicationChartRingPositions(cy, data, width, height);
            fitCommunicationChartViewport(cy);
            applySelectionStyles(selectedNodeIdRef.current, pinnedLinkRef.current);
            (_a = entranceAnimationRef.current) === null || _a === void 0 ? void 0 : _a.cancel();
            entranceAnimationRef.current = playCommunicationChartEntranceAnimation(cy, redrawCanvasOverlay);
            requestAnimationFrame(redrawCanvasOverlay);
        }
        catch (error) {
            if (process.env.NODE_ENV === 'development') {
                // eslint-disable-next-line no-console -- surfaced when debugging chart init failures
                console.error('Communication chart failed to load elements', error);
            }
            setChartInitError(true);
        }
    }, [data, applySelectionStyles, redrawCanvasOverlay, t]);
    useEffect(function () {
        var avatarUrls = data.nodes
            .map(function (node) { return node.avatarUrl; })
            .filter(function (url) { return Boolean(url); });
        if (avatarUrls.length === 0) {
            return;
        }
        var cancelled = false;
        var redrawRafId = null;
        var scheduleRedrawCanvasOverlay = function () {
            if (cancelled || redrawRafId !== null) {
                return;
            }
            redrawRafId = requestAnimationFrame(function () {
                redrawRafId = null;
                if (!cancelled) {
                    redrawCanvasOverlay();
                }
            });
        };
        void preloadCommunicationChartAvatarImages(avatarUrls, {
            onAvatarLoad: scheduleRedrawCanvasOverlay
        }).then(function () {
            scheduleRedrawCanvasOverlay();
        });
        return function () {
            cancelled = true;
            if (redrawRafId !== null) {
                cancelAnimationFrame(redrawRafId);
                redrawRafId = null;
            }
        };
    }, [data, redrawCanvasOverlay]);
    useEffect(function () {
        var cy = cyRef.current;
        if (!cy) {
            return;
        }
        cy.resize();
        var width = containerSize.width, height = containerSize.height;
        applyCommunicationChartRingPositions(cy, displayDataRef.current, width, height);
        fitCommunicationChartViewport(cy);
        applySelectionStyles(selectedNodeIdRef.current, pinnedLinkRef.current);
        redrawCanvasOverlay();
    }, [containerSize, applySelectionStyles, redrawCanvasOverlay]);
    useEffect(function () {
        applySelectionStyles(selectedNodeId, pinnedLink);
    }, [selectedNodeId, pinnedLink, pinnedHubTeamId, applySelectionStyles]);
    return {
        containerRef: containerRef,
        arrowOverlayRef: arrowOverlayRef,
        chartInitError: chartInitError,
        hoverTooltip: hoverTooltip,
        containerSize: containerSize
    };
};
